import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Rnd } from 'react-rnd';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { 
  ArrowLeft, Upload, Download, Send, Mail, MessageCircle, 
  Palette, Image, Users, Trash2, Plus, Check, X,
  Calendar, MapPin, Building2, Clock, FileText, Eye, Copy, Move, Maximize2,
  RotateCw, ZoomIn
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';

// 50 Background Images for Invitation Templates
const BACKGROUND_IMAGES = [
  // Corporate & Conference
  { id: 'bg-1', url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop', category: 'conference', name: 'Conference Hall' },
  { id: 'bg-2', url: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&h=600&fit=crop', category: 'conference', name: 'Business Meeting' },
  { id: 'bg-3', url: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=800&h=600&fit=crop', category: 'conference', name: 'Presentation' },
  { id: 'bg-4', url: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=800&h=600&fit=crop', category: 'conference', name: 'Stage Lights' },
  { id: 'bg-5', url: 'https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=800&h=600&fit=crop', category: 'conference', name: 'Empty Auditorium' },
  // Gala & Award
  { id: 'bg-6', url: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&h=600&fit=crop', category: 'gala', name: 'Gala Dinner' },
  { id: 'bg-7', url: 'https://images.unsplash.com/photo-1531545514256-b1400bc00f31?w=800&h=600&fit=crop', category: 'award', name: 'Award Ceremony' },
  { id: 'bg-8', url: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800&h=600&fit=crop', category: 'gala', name: 'Celebration Lights' },
  { id: 'bg-9', url: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=800&h=600&fit=crop', category: 'gala', name: 'Party Atmosphere' },
  { id: 'bg-10', url: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800&h=600&fit=crop', category: 'gala', name: 'Concert Crowd' },
  // Team Building & Offsite
  { id: 'bg-11', url: 'https://images.unsplash.com/photo-1527529482837-4698179dc6ce?w=800&h=600&fit=crop', category: 'offsite', name: 'Team Celebration' },
  { id: 'bg-12', url: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop', category: 'training', name: 'Workshop' },
  { id: 'bg-13', url: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=800&h=600&fit=crop', category: 'offsite', name: 'Team Hands' },
  { id: 'bg-14', url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop', category: 'offsite', name: 'Team Discussion' },
  { id: 'bg-15', url: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=800&h=600&fit=crop', category: 'offsite', name: 'Team Dinner' },
  // Nature & Retreat
  { id: 'bg-16', url: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&h=600&fit=crop', category: 'retreat', name: 'Resort Pool' },
  { id: 'bg-17', url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop', category: 'retreat', name: 'Mountain View' },
  { id: 'bg-18', url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&h=600&fit=crop', category: 'retreat', name: 'Beach Sunset' },
  { id: 'bg-19', url: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=600&fit=crop', category: 'retreat', name: 'Forest Path' },
  { id: 'bg-20', url: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=800&h=600&fit=crop', category: 'retreat', name: 'Ocean Waves' },
  // Elegant & Luxury
  { id: 'bg-21', url: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800&h=600&fit=crop', category: 'luxury', name: 'Wedding Venue' },
  { id: 'bg-22', url: 'https://images.unsplash.com/photo-1478146896981-b80fe463b330?w=800&h=600&fit=crop', category: 'luxury', name: 'Chandelier' },
  { id: 'bg-23', url: 'https://images.unsplash.com/photo-1544928147-79a2dbc1f389?w=800&h=600&fit=crop', category: 'luxury', name: 'Grand Ballroom' },
  { id: 'bg-24', url: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop', category: 'luxury', name: 'Fine Dining' },
  { id: 'bg-25', url: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&h=600&fit=crop', category: 'luxury', name: 'Luxury Hotel' },
  // Abstract & Modern
  { id: 'bg-26', url: 'https://images.unsplash.com/photo-1557683316-973673bdar25?w=800&h=600&fit=crop', category: 'modern', name: 'Abstract Blue' },
  { id: 'bg-27', url: 'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=800&h=600&fit=crop', category: 'modern', name: 'Gradient Colors' },
  { id: 'bg-28', url: 'https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=800&h=600&fit=crop', category: 'modern', name: 'Purple Gradient' },
  { id: 'bg-29', url: 'https://images.unsplash.com/photo-1557682224-5b8590cd9ec5?w=800&h=600&fit=crop', category: 'modern', name: 'Teal Gradient' },
  { id: 'bg-30', url: 'https://images.unsplash.com/photo-1557682260-96773eb01377?w=800&h=600&fit=crop', category: 'modern', name: 'Pink Gradient' },
  // City & Urban
  { id: 'bg-31', url: 'https://images.unsplash.com/photo-1514565131-fce0801e5785?w=800&h=600&fit=crop', category: 'urban', name: 'City Skyline Night' },
  { id: 'bg-32', url: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=800&h=600&fit=crop', category: 'urban', name: 'New York City' },
  { id: 'bg-33', url: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&h=600&fit=crop', category: 'urban', name: 'City Buildings' },
  { id: 'bg-34', url: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&h=600&fit=crop', category: 'urban', name: 'Street Lights' },
  { id: 'bg-35', url: 'https://images.unsplash.com/photo-1444723121867-7a241cacace9?w=800&h=600&fit=crop', category: 'urban', name: 'Bridge Night' },
  // Festive & Celebration
  { id: 'bg-36', url: 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800&h=600&fit=crop', category: 'festive', name: 'Confetti' },
  { id: 'bg-37', url: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=800&h=600&fit=crop', category: 'festive', name: 'Balloons' },
  { id: 'bg-38', url: 'https://images.unsplash.com/photo-1504196606672-aef5c9cefc92?w=800&h=600&fit=crop', category: 'festive', name: 'Party Lights' },
  { id: 'bg-39', url: 'https://images.unsplash.com/photo-1496843916299-590492c751f4?w=800&h=600&fit=crop', category: 'festive', name: 'Sparklers' },
  { id: 'bg-40', url: 'https://images.unsplash.com/photo-1533294455009-a77b7557d2d1?w=800&h=600&fit=crop', category: 'festive', name: 'Fireworks' },
  // Minimalist & Clean
  { id: 'bg-41', url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop', category: 'minimal', name: 'White Marble' },
  { id: 'bg-42', url: 'https://images.unsplash.com/photo-1553095066-5014bc7b7f2d?w=800&h=600&fit=crop', category: 'minimal', name: 'Light Abstract' },
  { id: 'bg-43', url: 'https://images.unsplash.com/photo-1557683311-eac922347aa1?w=800&h=600&fit=crop', category: 'minimal', name: 'Soft Blue' },
  { id: 'bg-44', url: 'https://images.unsplash.com/photo-1557682257-2f9c37a3a5f3?w=800&h=600&fit=crop', category: 'minimal', name: 'Clean White' },
  { id: 'bg-45', url: 'https://images.unsplash.com/photo-1534796636912-3b95b3ab5986?w=800&h=600&fit=crop', category: 'minimal', name: 'Dark Gradient' },
  // Traditional & Cultural
  { id: 'bg-46', url: 'https://images.unsplash.com/photo-1464366400600-7168b8af9bc3?w=800&h=600&fit=crop', category: 'traditional', name: 'Lanterns' },
  { id: 'bg-47', url: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=800&h=600&fit=crop', category: 'traditional', name: 'Diwali Lights' },
  { id: 'bg-48', url: 'https://images.unsplash.com/photo-1514222134-b57cbb8ce073?w=800&h=600&fit=crop', category: 'traditional', name: 'Marigold Flowers' },
  { id: 'bg-49', url: 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=800&h=600&fit=crop', category: 'traditional', name: 'Temple' },
  { id: 'bg-50', url: 'https://images.unsplash.com/photo-1517263904808-5dc91e3e7044?w=800&h=600&fit=crop', category: 'traditional', name: 'Golden Pattern' },
];

const BACKGROUND_CATEGORIES = [
  { id: 'all', name: 'All' },
  { id: 'conference', name: 'Conference' },
  { id: 'gala', name: 'Gala' },
  { id: 'offsite', name: 'Offsite' },
  { id: 'retreat', name: 'Retreat' },
  { id: 'luxury', name: 'Luxury' },
  { id: 'modern', name: 'Modern' },
  { id: 'urban', name: 'City' },
  { id: 'festive', name: 'Festive' },
  { id: 'minimal', name: 'Minimal' },
  { id: 'traditional', name: 'Traditional' },
];

export default function InvitationBuilderPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const invitationRef = useRef(null);
  const previewContainerRef = useRef(null);

  // Background selection
  const [selectedBackground, setSelectedBackground] = useState(BACKGROUND_IMAGES[0]);
  const [backgroundCategory, setBackgroundCategory] = useState('all');
  
  // Invitation data
  const [invitation, setInvitation] = useState({
    event_name: 'Annual Sales Conference 2026',
    event_date: '2026-03-15',
    event_time: '10:00',
    venue_name: 'Grand Hyatt Mumbai',
    venue_address: 'Off Western Express Highway, Santacruz East',
    company_name: 'Your Company',
    company_logo: null,
    logo_filter: 'original', // original, black, white
    custom_message: 'You are cordially invited to join us for this special event.',
    rsvp_email: 'rsvp@company.com',
    rsvp_phone: '+91 98765 43210',
    background_image: BACKGROUND_IMAGES[0].url,
    text_color: '#ffffff',
    accent_color: '#d4af37',
    overlay_color: '#000000',
    overlay_opacity: 0.5,
  });
  
  // Processed logo URL (with canvas processing for color effects)
  const [processedLogoUrl, setProcessedLogoUrl] = useState(null);

  // Element positions for drag & drop
  const [elements, setElements] = useState({
    logo: { x: 160, y: 20, width: 80, height: 60, visible: true },
    companyName: { x: 50, y: 90, width: 300, height: 30, visible: true },
    eventTitle: { x: 30, y: 140, width: 340, height: 60, visible: true },
    message: { x: 50, y: 210, width: 300, height: 50, visible: true },
    dateTime: { x: 100, y: 280, width: 200, height: 30, visible: true },
    venue: { x: 80, y: 320, width: 240, height: 50, visible: true },
    rsvp: { x: 100, y: 390, width: 200, height: 50, visible: true },
  });

  const [selectedElement, setSelectedElement] = useState(null);

  // Process logo with canvas when color filter changes
  useEffect(() => {
    if (!invitation.company_logo) {
      setProcessedLogoUrl(null);
      return;
    }
    
    const processLogo = () => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        
        // Draw the original image
        ctx.drawImage(img, 0, 0);
        
        // Get image data for pixel manipulation
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Process each pixel based on filter
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const a = data[i + 3];
          
          // Check if pixel is close to white (for transparency)
          const isWhite = r > 240 && g > 240 && b > 240;
          
          // Make white pixels transparent
          if (isWhite && a > 0) {
            data[i + 3] = 0;
          } else if (a > 0) {
            // Apply color filter
            if (invitation.logo_filter === 'black') {
              data[i] = 0;     // R
              data[i + 1] = 0; // G
              data[i + 2] = 0; // B
            } else if (invitation.logo_filter === 'white') {
              data[i] = 255;     // R
              data[i + 1] = 255; // G
              data[i + 2] = 255; // B
            }
            // 'original' keeps the original colors
          }
        }
        
        // Put the processed data back
        ctx.putImageData(imageData, 0, 0);
        
        // Convert to data URL
        setProcessedLogoUrl(canvas.toDataURL('image/png'));
      };
      
      img.onerror = () => {
        // If processing fails, use original
        setProcessedLogoUrl(invitation.company_logo);
      };
      
      img.src = invitation.company_logo;
    };
    
    processLogo();
  }, [invitation.company_logo, invitation.logo_filter]);

  // Recipients
  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState({ email: '', name: '' });
  const [bulkEmails, setBulkEmails] = useState('');
  
  // UI state
  const [activeTab, setActiveTab] = useState('template');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [savedInvitationId, setSavedInvitationId] = useState(null);

  // Load from booking if bookingId provided
  useEffect(() => {
    const bookingId = searchParams.get('bookingId');
    if (bookingId) {
      loadFromBooking(bookingId);
    }
  }, [searchParams]);

  const loadFromBooking = async (bookingId) => {
    try {
      const response = await api.getInvitationFromBooking(bookingId);
      const data = response.data;
      setInvitation(prev => ({
        ...prev,
        event_name: data.event_name || prev.event_name,
        event_date: data.event_date || prev.event_date,
        venue_name: data.venue_name || prev.venue_name,
        venue_address: data.venue_address || prev.venue_address,
        company_name: data.company_name || prev.company_name,
      }));
      toast.success('Event details loaded from booking!');
    } catch (error) {
      console.error('Failed to load booking:', error);
    }
  };

  const handleBackgroundSelect = (bg) => {
    setSelectedBackground(bg);
    setInvitation(prev => ({
      ...prev,
      background_image: bg.url,
    }));
  };

  const handleInputChange = (field, value) => {
    setInvitation(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setInvitation(prev => ({ ...prev, company_logo: reader.result }));
        toast.success('Logo uploaded!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleElementDrag = useCallback((elementId, d) => {
    setElements(prev => ({
      ...prev,
      [elementId]: { ...prev[elementId], x: d.x, y: d.y }
    }));
  }, []);

  const handleElementResize = useCallback((elementId, ref, position) => {
    setElements(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        width: parseInt(ref.style.width),
        height: parseInt(ref.style.height),
        x: position.x,
        y: position.y,
      }
    }));
  }, []);

  // Get logo style for CSS filter (fallback for simple cases)
  const getLogoStyle = useCallback(() => {
    // We use canvas processing now, but keep CSS filter as fallback
    return {};
  }, []);

  const handleAddRecipient = () => {
    if (newRecipient.email && newRecipient.email.includes('@')) {
      if (!recipients.find(r => r.email === newRecipient.email)) {
        setRecipients([...recipients, { ...newRecipient, sent: false }]);
        setNewRecipient({ email: '', name: '' });
      } else {
        toast.error('This email is already added');
      }
    } else {
      toast.error('Please enter a valid email');
    }
  };

  const handleRemoveRecipient = (email) => {
    setRecipients(recipients.filter(r => r.email !== email));
  };

  const handleBulkEmailPaste = () => {
    if (!bulkEmails.trim()) {
      toast.error('Please paste email addresses first');
      return;
    }
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const foundEmails = bulkEmails.match(emailRegex) || [];
    const newRecipients = [];
    const existingEmails = new Set(recipients.map(r => r.email.toLowerCase()));
    foundEmails.forEach(email => {
      const lowerEmail = email.toLowerCase();
      if (!existingEmails.has(lowerEmail)) {
        newRecipients.push({ email: lowerEmail, name: '', sent: false });
        existingEmails.add(lowerEmail);
      }
    });
    if (newRecipients.length > 0) {
      setRecipients([...recipients, ...newRecipients]);
      toast.success(`Added ${newRecipients.length} email(s)`);
    } else {
      toast.error('No new valid emails found');
    }
    setBulkEmails('');
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await api.parseCSVRecipients(formData);
        const newRecipients = response.data.recipients.filter(
          r => !recipients.find(existing => existing.email === r.email)
        );
        setRecipients([...recipients, ...newRecipients.map(r => ({ ...r, sent: false }))]);
        toast.success(`Added ${newRecipients.length} recipients from CSV`);
      } catch (error) {
        toast.error('Failed to parse CSV file');
      }
    }
  };

  const handleSaveInvitation = async () => {
    setIsLoading(true);
    try {
      const data = {
        ...invitation,
        template_id: selectedBackground?.id || 'custom',
        elements: elements,
      };
      
      let response;
      if (savedInvitationId) {
        response = await api.updateInvitation(savedInvitationId, data);
      } else {
        response = await api.createInvitation(data);
        setSavedInvitationId(response.data.id);
      }
      
      if (recipients.length > 0) {
        await api.addInvitationRecipients(response.data.id, { recipients });
      }
      
      toast.success('Invitation saved!');
    } catch (error) {
      toast.error('Failed to save invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!invitationRef.current) return;
    try {
      setSelectedElement(null); // Hide selection borders
      await new Promise(resolve => setTimeout(resolve, 100));
      const canvas = await html2canvas(invitationRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
      });
      const link = document.createElement('a');
      link.download = `invitation-${invitation.event_name || 'event'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Invitation downloaded!');
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const handleSendEmail = async () => {
    if (recipients.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    await handleSaveInvitation();
    if (!savedInvitationId) return;
    
    setIsSending(true);
    try {
      const response = await api.sendInvitation(savedInvitationId, { method: 'email' });
      toast.success(`Sent to ${response.data.sent_count} recipients!`);
      setRecipients(recipients.map(r => ({ ...r, sent: true })));
    } catch (error) {
      toast.error('Failed to send');
    } finally {
      setIsSending(false);
    }
  };

  const handleShareWhatsApp = () => {
    const message = `You're invited to ${invitation.event_name}!\n\n📅 ${invitation.event_date}\n⏰ ${invitation.event_time}\n📍 ${invitation.venue_name}\n${invitation.venue_address}\n\n${invitation.custom_message}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getLogoStyleOld = () => {
    switch (invitation.logo_filter) {
      case 'grayscale': return { filter: 'grayscale(100%)' };
      case 'invert': return { filter: 'invert(100%)' };
      case 'brightness': return { filter: 'brightness(0) invert(1)' };
      default: return {};
    }
  };

  const filteredBackgrounds = backgroundCategory === 'all' 
    ? BACKGROUND_IMAGES 
    : BACKGROUND_IMAGES.filter(bg => bg.category === backgroundCategory);

  // Memoized render function for the invitation preview
  const renderInvitationPreview = useCallback((isEditable = false, scale = 1) => {
    // Use processed logo or original
    const displayLogoUrl = processedLogoUrl || invitation.company_logo;
    
    // Text and accent colors for direct styling
    const textColor = invitation.text_color || '#ffffff';
    const accentColor = invitation.accent_color || '#d4af37';
    
    return (
      <div 
        ref={isEditable ? invitationRef : null}
        className="relative overflow-hidden rounded-lg shadow-xl"
        style={{ 
          width: 400 * scale,
          height: 500 * scale,
          backgroundColor: '#1e3a5f',
        }}
        onClick={() => isEditable && setSelectedElement(null)}
      >
        {/* Background Image */}
        {invitation.background_image && (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${invitation.background_image})` }}
          />
        )}
        
        {/* Overlay */}
        <div 
          className="absolute inset-0"
          style={{ 
            backgroundColor: invitation.overlay_color,
            opacity: invitation.overlay_opacity
          }}
        />
        
        {/* Draggable Elements */}
        <div className="absolute inset-0" style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          {/* Logo - Show actual logo image, not just text */}
          {invitation.company_logo && elements.logo.visible && (
            isEditable ? (
              <Rnd
                size={{ width: elements.logo.width, height: elements.logo.height }}
                position={{ x: elements.logo.x, y: elements.logo.y }}
                onDragStop={(e, d) => handleElementDrag('logo', d)}
                onResizeStop={(e, dir, ref, delta, pos) => handleElementResize('logo', ref, pos)}
                bounds="parent"
                minWidth={40}
                minHeight={30}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('logo'); }}
                className={`cursor-move ${selectedElement === 'logo' ? 'ring-2 ring-gold ring-offset-2' : ''}`}
                style={{ zIndex: selectedElement === 'logo' ? 100 : 10 }}
              >
                <div className="w-full h-full flex items-center justify-center p-1">
                  <img 
                    src={displayLogoUrl} 
                    alt="Logo" 
                    className="max-w-full max-h-full object-contain"
                    style={{ 
                      pointerEvents: 'none',
                      userSelect: 'none'
                    }}
                    draggable={false}
                  />
                </div>
                {selectedElement === 'logo' && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gold text-navy px-2 py-0.5 rounded text-xs flex items-center gap-1 whitespace-nowrap shadow">
                    <Move className="w-3 h-3" /> Drag to move
                  </div>
                )}
              </Rnd>
            ) : (
              <div style={{ position: 'absolute', left: elements.logo.x, top: elements.logo.y, width: elements.logo.width, height: elements.logo.height }} className="flex items-center justify-center p-1">
                <img src={displayLogoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
              </div>
            )
          )}

          {/* Company Name */}
          {invitation.company_name && elements.companyName.visible && (
            isEditable ? (
              <Rnd
                size={{ width: elements.companyName.width, height: elements.companyName.height }}
                position={{ x: elements.companyName.x, y: elements.companyName.y }}
                onDragStop={(e, d) => handleElementDrag('companyName', d)}
                onResizeStop={(e, dir, ref, delta, pos) => handleElementResize('companyName', ref, pos)}
                bounds="parent"
                minWidth={100}
                minHeight={20}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('companyName'); }}
                className={`cursor-move ${selectedElement === 'companyName' ? 'ring-2 ring-gold' : ''}`}
                style={{ zIndex: selectedElement === 'companyName' ? 100 : 10 }}
              >
                <p className="text-xs uppercase tracking-widest text-center opacity-80 w-full h-full flex items-center justify-center" style={{ color: textColor }}>
                  {invitation.company_name}
                </p>
                {selectedElement === 'companyName' && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gold text-navy px-2 py-0.5 rounded text-xs flex items-center gap-1 whitespace-nowrap shadow">
                    <Move className="w-3 h-3" /> Drag
                  </div>
                )}
              </Rnd>
            ) : (
              <div style={{ position: 'absolute', left: elements.companyName.x, top: elements.companyName.y, width: elements.companyName.width }}>
                <p className="text-xs uppercase tracking-widest text-center opacity-80" style={{ color: textColor }}>{invitation.company_name}</p>
              </div>
            )
          )}

          {/* Event Title */}
          {elements.eventTitle.visible && (
            isEditable ? (
              <Rnd
                size={{ width: elements.eventTitle.width, height: elements.eventTitle.height }}
                position={{ x: elements.eventTitle.x, y: elements.eventTitle.y }}
                onDragStop={(e, d) => handleElementDrag('eventTitle', d)}
                onResizeStop={(e, dir, ref, delta, pos) => handleElementResize('eventTitle', ref, pos)}
                bounds="parent"
                minWidth={150}
                minHeight={40}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('eventTitle'); }}
                className={`cursor-move ${selectedElement === 'eventTitle' ? 'ring-2 ring-gold' : ''}`}
                style={{ zIndex: selectedElement === 'eventTitle' ? 100 : 10 }}
              >
                <div className="text-center w-full h-full flex flex-col items-center justify-center">
                  <p className="text-sm mb-1" style={{ color: accentColor }}>You&apos;re Invited</p>
                  <h2 className="text-xl font-bold leading-tight" style={{ color: textColor }}>
                    {invitation.event_name || 'Event Name'}
                  </h2>
                </div>
                {selectedElement === 'eventTitle' && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gold text-navy px-2 py-0.5 rounded text-xs flex items-center gap-1 whitespace-nowrap shadow">
                    <Move className="w-3 h-3" /> Drag
                  </div>
                )}
              </Rnd>
            ) : (
              <div style={{ position: 'absolute', left: elements.eventTitle.x, top: elements.eventTitle.y, width: elements.eventTitle.width }} className="text-center">
                <p className="text-sm mb-1" style={{ color: accentColor }}>You&apos;re Invited</p>
                <h2 className="text-xl font-bold leading-tight" style={{ color: textColor }}>{invitation.event_name || 'Event Name'}</h2>
              </div>
            )
          )}

          {/* Custom Message */}
          {invitation.custom_message && elements.message.visible && (
            isEditable ? (
              <Rnd
                size={{ width: elements.message.width, height: elements.message.height }}
                position={{ x: elements.message.x, y: elements.message.y }}
                onDragStop={(e, d) => handleElementDrag('message', d)}
                onResizeStop={(e, dir, ref, delta, pos) => handleElementResize('message', ref, pos)}
                bounds="parent"
                minWidth={150}
                minHeight={30}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('message'); }}
                className={`cursor-move ${selectedElement === 'message' ? 'ring-2 ring-gold' : ''}`}
                style={{ zIndex: selectedElement === 'message' ? 100 : 10 }}
              >
                <p className="text-xs text-center opacity-90 w-full h-full flex items-center justify-center" style={{ color: textColor }}>
                  {invitation.custom_message}
                </p>
                {selectedElement === 'message' && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gold text-navy px-2 py-0.5 rounded text-xs flex items-center gap-1 whitespace-nowrap shadow">
                    <Move className="w-3 h-3" /> Drag
                  </div>
                )}
              </Rnd>
            ) : (
              <div style={{ position: 'absolute', left: elements.message.x, top: elements.message.y, width: elements.message.width }}>
                <p className="text-xs text-center opacity-90" style={{ color: textColor }}>{invitation.custom_message}</p>
              </div>
            )
          )}

          {/* Date & Time */}
          {(invitation.event_date || invitation.event_time) && elements.dateTime.visible && (
            isEditable ? (
              <Rnd
                size={{ width: elements.dateTime.width, height: elements.dateTime.height }}
                position={{ x: elements.dateTime.x, y: elements.dateTime.y }}
                onDragStop={(e, d) => handleElementDrag('dateTime', d)}
                onResizeStop={(e, dir, ref, delta, pos) => handleElementResize('dateTime', ref, pos)}
                bounds="parent"
                minWidth={150}
                minHeight={25}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('dateTime'); }}
                className={`cursor-move ${selectedElement === 'dateTime' ? 'ring-2 ring-gold' : ''}`}
                style={{ zIndex: selectedElement === 'dateTime' ? 100 : 10 }}
              >
                <div className="flex items-center justify-center gap-4 text-xs w-full h-full" style={{ color: textColor }}>
                  {invitation.event_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" style={{ color: accentColor }} />
                      {new Date(invitation.event_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                  {invitation.event_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" style={{ color: accentColor }} />
                      {invitation.event_time}
                    </span>
                  )}
                </div>
                {selectedElement === 'dateTime' && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gold text-navy px-2 py-0.5 rounded text-xs flex items-center gap-1 whitespace-nowrap shadow">
                    <Move className="w-3 h-3" /> Drag
                  </div>
                )}
              </Rnd>
            ) : (
              <div style={{ position: 'absolute', left: elements.dateTime.x, top: elements.dateTime.y, width: elements.dateTime.width }}>
                <div className="flex items-center justify-center gap-4 text-xs" style={{ color: textColor }}>
                  {invitation.event_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" style={{ color: accentColor }} />{new Date(invitation.event_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                  {invitation.event_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" style={{ color: accentColor }} />{invitation.event_time}</span>}
                </div>
              </div>
            )
          )}

          {/* Venue */}
          {(invitation.venue_name || invitation.venue_address) && elements.venue.visible && (
            isEditable ? (
              <Rnd
                size={{ width: elements.venue.width, height: elements.venue.height }}
                position={{ x: elements.venue.x, y: elements.venue.y }}
                onDragStop={(e, d) => handleElementDrag('venue', d)}
                onResizeStop={(e, dir, ref, delta, pos) => handleElementResize('venue', ref, pos)}
                bounds="parent"
                minWidth={150}
                minHeight={35}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('venue'); }}
                className={`cursor-move ${selectedElement === 'venue' ? 'ring-2 ring-gold' : ''}`}
                style={{ zIndex: selectedElement === 'venue' ? 100 : 10 }}
              >
                <div className="text-center text-xs w-full h-full flex flex-col items-center justify-center" style={{ color: textColor }}>
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <MapPin className="w-3 h-3" style={{ color: accentColor }} />
                    <span className="font-medium">{invitation.venue_name}</span>
                  </div>
                  {invitation.venue_address && <p className="opacity-70 text-[10px]">{invitation.venue_address}</p>}
                </div>
                {selectedElement === 'venue' && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gold text-navy px-2 py-0.5 rounded text-xs flex items-center gap-1 whitespace-nowrap shadow">
                    <Move className="w-3 h-3" /> Drag
                  </div>
                )}
              </Rnd>
            ) : (
              <div style={{ position: 'absolute', left: elements.venue.x, top: elements.venue.y, width: elements.venue.width }} className="text-center text-xs">
                <div className="flex items-center justify-center gap-1 mb-1" style={{ color: textColor }}>
                  <MapPin className="w-3 h-3" style={{ color: accentColor }} />
                  <span className="font-medium">{invitation.venue_name}</span>
                </div>
                {invitation.venue_address && <p className="opacity-70 text-[10px]" style={{ color: textColor }}>{invitation.venue_address}</p>}
              </div>
            )
          )}

          {/* RSVP */}
          {(invitation.rsvp_email || invitation.rsvp_phone) && elements.rsvp.visible && (
            isEditable ? (
              <Rnd
                size={{ width: elements.rsvp.width, height: elements.rsvp.height }}
                position={{ x: elements.rsvp.x, y: elements.rsvp.y }}
                onDragStop={(e, d) => handleElementDrag('rsvp', d)}
                onResizeStop={(e, dir, ref, delta, pos) => handleElementResize('rsvp', ref, pos)}
                bounds="parent"
                minWidth={150}
                minHeight={40}
                onClick={(e) => { e.stopPropagation(); setSelectedElement('rsvp'); }}
                className={`cursor-move ${selectedElement === 'rsvp' ? 'ring-2 ring-gold' : ''}`}
                style={{ zIndex: selectedElement === 'rsvp' ? 100 : 10 }}
              >
                <div className="text-center pt-2 border-t w-full h-full flex flex-col items-center justify-center" style={{ borderColor: `${accentColor}40`, color: textColor }}>
                  <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: accentColor }}>RSVP</p>
                  {invitation.rsvp_email && <p className="text-xs">{invitation.rsvp_email}</p>}
                  {invitation.rsvp_phone && <p className="text-xs">{invitation.rsvp_phone}</p>}
                </div>
                {selectedElement === 'rsvp' && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gold text-navy px-2 py-0.5 rounded text-xs flex items-center gap-1 whitespace-nowrap shadow">
                    <Move className="w-3 h-3" /> Drag
                  </div>
                )}
              </Rnd>
            ) : (
              <div style={{ position: 'absolute', left: elements.rsvp.x, top: elements.rsvp.y, width: elements.rsvp.width }}>
                <div className="text-center pt-2 border-t" style={{ borderColor: `${accentColor}40`, color: textColor }}>
                  <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: accentColor }}>RSVP</p>
                  {invitation.rsvp_email && <p className="text-xs">{invitation.rsvp_email}</p>}
                  {invitation.rsvp_phone && <p className="text-xs">{invitation.rsvp_phone}</p>}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    );
  }, [invitation, elements, selectedElement, processedLogoUrl, handleElementDrag, handleElementResize]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-navy text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/client')} className="text-white hover:bg-navy-light">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Invitation Card Builder</h1>
            <Badge variant="outline" className="border-gold text-gold">
              <Move className="w-3 h-3 mr-1" /> Drag to move • <Maximize2 className="w-3 h-3 mx-1" /> Drag corners to resize
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setShowPreview(true)} className="border-gold text-gold hover:bg-gold hover:text-navy">
              <Eye className="w-4 h-4 mr-2" /> Full Preview
            </Button>
            <Button onClick={handleSaveInvitation} disabled={isLoading} className="bg-gold text-navy hover:bg-gold-light">
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <div className="flex gap-6 items-start relative">
          {/* Left Panel - Editor */}
          <div className="flex-1 min-w-0 pr-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="template">Background</TabsTrigger>
                <TabsTrigger value="customize">Details</TabsTrigger>
                <TabsTrigger value="recipients">Recipients</TabsTrigger>
                <TabsTrigger value="send">Send</TabsTrigger>
              </TabsList>

              {/* Background Selection */}
              <TabsContent value="template" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Image className="w-5 h-5 text-gold" />
                      Choose Background ({BACKGROUND_IMAGES.length} options)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {BACKGROUND_CATEGORIES.map(cat => (
                        <Button key={cat.id} variant={backgroundCategory === cat.id ? 'default' : 'outline'} size="sm" onClick={() => setBackgroundCategory(cat.id)} className={backgroundCategory === cat.id ? 'bg-navy' : ''}>
                          {cat.name}
                        </Button>
                      ))}
                    </div>
                    <div className="grid grid-cols-4 gap-2 max-h-[400px] overflow-y-auto">
                      {filteredBackgrounds.map((bg) => (
                        <div key={bg.id} onClick={() => handleBackgroundSelect(bg)} className={`cursor-pointer rounded-lg overflow-hidden border-2 aspect-[4/3] relative ${selectedBackground?.id === bg.id ? 'border-gold ring-2 ring-gold/30' : 'border-slate-200 hover:border-slate-400'}`}>
                          <img src={bg.url} alt={bg.name} className="w-full h-full object-cover" loading="lazy" />
                          {selectedBackground?.id === bg.id && <div className="absolute top-1 right-1 bg-gold rounded-full p-0.5"><Check className="w-3 h-3 text-navy" /></div>}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Customization */}
              <TabsContent value="customize" className="mt-4 space-y-4">
                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><FileText className="w-5 h-5 text-gold" />Event Details</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div><Label>Event Name</Label><Input value={invitation.event_name} onChange={(e) => handleInputChange('event_name', e.target.value)} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Date</Label><Input type="date" value={invitation.event_date} onChange={(e) => handleInputChange('event_date', e.target.value)} /></div>
                      <div><Label>Time</Label><Input type="time" value={invitation.event_time} onChange={(e) => handleInputChange('event_time', e.target.value)} /></div>
                    </div>
                    <div><Label>Venue Name</Label><Input value={invitation.venue_name} onChange={(e) => handleInputChange('venue_name', e.target.value)} /></div>
                    <div><Label>Venue Address</Label><Input value={invitation.venue_address} onChange={(e) => handleInputChange('venue_address', e.target.value)} /></div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Building2 className="w-5 h-5 text-gold" />Company & Logo</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div><Label>Company Name</Label><Input value={invitation.company_name} onChange={(e) => handleInputChange('company_name', e.target.value)} /></div>
                    <div>
                      <Label>Company Logo</Label>
                      <div className="mt-2">
                        {invitation.company_logo ? (
                          <div className="space-y-4">
                            {/* Logo Preview Box */}
                            <div className="flex items-start gap-4">
                              <div className="relative">
                                {/* Show processed logo preview with checkerboard background for transparency */}
                                <div 
                                  className="w-24 h-24 rounded-lg border-2 border-slate-200 flex items-center justify-center p-2"
                                  style={{ 
                                    backgroundImage: 'linear-gradient(45deg, #e5e7eb 25%, transparent 25%), linear-gradient(-45deg, #e5e7eb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e7eb 75%), linear-gradient(-45deg, transparent 75%, #e5e7eb 75%)',
                                    backgroundSize: '10px 10px',
                                    backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px'
                                  }}
                                >
                                  <img 
                                    src={processedLogoUrl || invitation.company_logo} 
                                    alt="Logo Preview" 
                                    className="max-w-full max-h-full object-contain"
                                  />
                                </div>
                                <button 
                                  onClick={() => {
                                    handleInputChange('company_logo', null);
                                    setProcessedLogoUrl(null);
                                  }} 
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                              <div className="flex-1 space-y-3">
                                <div>
                                  <Label className="text-xs font-medium text-slate-700 block mb-2">Logo Color Style</Label>
                                  <div className="flex gap-2">
                                    <Button 
                                      size="sm" 
                                      variant={invitation.logo_filter === 'original' ? 'default' : 'outline'} 
                                      onClick={() => handleInputChange('logo_filter', 'original')} 
                                      className={`flex-1 ${invitation.logo_filter === 'original' ? 'bg-navy text-white' : ''}`}
                                    >
                                      <div className="w-3 h-3 rounded-full bg-gradient-to-br from-red-500 via-green-500 to-blue-500 mr-1"></div>
                                      Original
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant={invitation.logo_filter === 'black' ? 'default' : 'outline'} 
                                      onClick={() => handleInputChange('logo_filter', 'black')} 
                                      className={`flex-1 ${invitation.logo_filter === 'black' ? 'bg-navy text-white' : ''}`}
                                    >
                                      <div className="w-3 h-3 rounded-full bg-black mr-1"></div>
                                      Black
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant={invitation.logo_filter === 'white' ? 'default' : 'outline'} 
                                      onClick={() => handleInputChange('logo_filter', 'white')} 
                                      className={`flex-1 ${invitation.logo_filter === 'white' ? 'bg-navy text-white' : ''}`}
                                    >
                                      <div className="w-3 h-3 rounded-full bg-white border border-slate-300 mr-1"></div>
                                      White
                                    </Button>
                                  </div>
                                </div>
                                <p className="text-xs text-slate-500">
                                  ✓ White background automatically removed for transparency
                                </p>
                              </div>
                            </div>
                            {/* Change logo button */}
                            <label className="flex items-center justify-center gap-2 px-3 py-2 border border-slate-300 rounded-lg cursor-pointer hover:border-gold hover:bg-slate-50 text-sm text-slate-600">
                              <Upload className="w-4 h-4" />
                              <span>Change Logo</span>
                              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                            </label>
                          </div>
                        ) : (
                          <label className="flex items-center gap-3 px-4 py-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-gold hover:bg-slate-50">
                            <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                              <Upload className="w-6 h-6 text-slate-400" />
                            </div>
                            <div>
                              <span className="text-sm font-medium text-slate-700 block">Upload Company Logo</span>
                              <span className="text-xs text-slate-500">PNG, JPG, GIF (white background will be removed)</span>
                            </div>
                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                          </label>
                        )}
                      </div>
                    </div>
                    <div><Label>Custom Message</Label><Textarea value={invitation.custom_message} onChange={(e) => handleInputChange('custom_message', e.target.value)} rows={2} /></div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Palette className="w-5 h-5 text-gold" />Colors & Overlay</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label>Overlay Darkness: {Math.round(invitation.overlay_opacity * 100)}%</Label>
                      <input type="range" min="0" max="0.8" step="0.05" value={invitation.overlay_opacity} onChange={(e) => handleInputChange('overlay_opacity', parseFloat(e.target.value))} className="w-full" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label className="text-xs">Text</Label><div className="flex gap-2 mt-1"><input type="color" value={invitation.text_color} onChange={(e) => handleInputChange('text_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" /></div></div>
                      <div><Label className="text-xs">Accent</Label><div className="flex gap-2 mt-1"><input type="color" value={invitation.accent_color} onChange={(e) => handleInputChange('accent_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" /></div></div>
                      <div><Label className="text-xs">Overlay</Label><div className="flex gap-2 mt-1"><input type="color" value={invitation.overlay_color} onChange={(e) => handleInputChange('overlay_color', e.target.value)} className="w-8 h-8 rounded cursor-pointer" /></div></div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Mail className="w-5 h-5 text-gold" />RSVP</CardTitle></CardHeader>
                  <CardContent className="grid grid-cols-2 gap-3">
                    <div><Label>Email</Label><Input type="email" value={invitation.rsvp_email} onChange={(e) => handleInputChange('rsvp_email', e.target.value)} /></div>
                    <div><Label>Phone</Label><Input type="tel" value={invitation.rsvp_phone} onChange={(e) => handleInputChange('rsvp_phone', e.target.value)} /></div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Recipients */}
              <TabsContent value="recipients" className="mt-4">
                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-gold" />Recipients ({recipients.length})</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-2"><Copy className="w-4 h-4 text-blue-600" /><span className="text-sm font-medium text-blue-800">Paste Multiple Emails</span></div>
                      <Textarea placeholder="Paste emails (comma, semicolon, newline separated)" value={bulkEmails} onChange={(e) => setBulkEmails(e.target.value)} rows={3} className="text-sm" />
                      <Button onClick={handleBulkEmailPaste} size="sm" className="w-full mt-2 bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1" />Add Emails</Button>
                    </div>
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <label className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed rounded-lg cursor-pointer hover:border-gold bg-white">
                        <Upload className="w-4 h-4" /><span className="text-sm">Upload CSV</span>
                        <input type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" />
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <Input placeholder="Email" value={newRecipient.email} onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })} />
                      <Input placeholder="Name" value={newRecipient.name} onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })} />
                      <Button onClick={handleAddRecipient} size="icon" className="bg-gold text-navy"><Plus className="w-4 h-4" /></Button>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {recipients.map((r, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-navy text-white rounded-full flex items-center justify-center text-xs">{(r.name || r.email)[0].toUpperCase()}</div>
                            <div><p className="text-sm font-medium">{r.name || 'No name'}</p><p className="text-xs text-slate-500">{r.email}</p></div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => handleRemoveRecipient(r.email)} className="text-red-500 h-7 w-7"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Send */}
              <TabsContent value="send" className="mt-4">
                <Card>
                  <CardHeader><CardTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-gold" />Send Invitations</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <button onClick={handleDownload} className="flex items-center gap-4 p-4 border-2 rounded-lg hover:border-gold w-full text-left">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"><Download className="w-5 h-5 text-blue-600" /></div>
                      <div><p className="font-semibold text-navy">Download PNG</p><p className="text-sm text-slate-500">Save to share manually</p></div>
                    </button>
                    <button onClick={handleSendEmail} disabled={isSending || recipients.length === 0} className="flex items-center gap-4 p-4 border-2 rounded-lg hover:border-gold w-full text-left disabled:opacity-50">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center"><Mail className="w-5 h-5 text-green-600" /></div>
                      <div className="flex-1"><p className="font-semibold text-navy">{isSending ? 'Sending...' : 'Send Email'}</p><p className="text-sm text-slate-500">{recipients.length} recipients</p></div>
                    </button>
                    <button onClick={handleShareWhatsApp} className="flex items-center gap-4 p-4 border-2 rounded-lg hover:border-gold w-full text-left">
                      <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center"><MessageCircle className="w-5 h-5 text-emerald-600" /></div>
                      <div><p className="font-semibold text-navy">Share WhatsApp</p><p className="text-sm text-slate-500">Open with message</p></div>
                    </button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel - STICKY Preview */}
          <div className="w-[440px] flex-shrink-0 sticky top-24 h-fit" style={{ alignSelf: 'flex-start' }}>
            <Card className="shadow-lg border-2 border-slate-200">
              <CardHeader className="pb-2 bg-slate-50 rounded-t-lg">
                <CardTitle className="text-lg flex items-center gap-2 text-navy">
                  <Eye className="w-5 h-5 text-gold" />
                  Live Preview
                  <Badge variant="outline" className="ml-auto text-xs border-gold text-gold">Drag elements to reposition</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 bg-white">
                {renderInvitationPreview(true)}
                {selectedElement && (
                  <p className="text-center text-xs text-slate-500 mt-3 bg-slate-50 rounded-lg py-2">
                    Selected: <span className="font-semibold text-navy capitalize">{selectedElement}</span> - Drag to move, drag corners to resize
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Full Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-[500px] p-0 overflow-hidden bg-slate-100">
          <DialogHeader className="p-4 pb-2 bg-white border-b">
            <DialogTitle className="text-navy flex items-center gap-2">
              <Eye className="w-5 h-5 text-gold" />
              Full Preview
            </DialogTitle>
            <DialogDescription className="text-slate-500">Final invitation look - this is how your invitation will appear</DialogDescription>
          </DialogHeader>
          <div className="p-6 flex justify-center bg-slate-100">
            {renderInvitationPreview(false)}
          </div>
          <div className="p-4 bg-white border-t flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowPreview(false)} className="border-slate-300">
              Close
            </Button>
            <Button onClick={handleDownload} className="bg-gold text-navy hover:bg-gold-light">
              <Download className="w-4 h-4 mr-2" />
              Download PNG
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
